import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  FaCircleCheck,
  FaCircleXmark,
  FaChevronDown,
  FaGear
} from 'react-icons/fa6';
import {
  ZeitschritteStatus,
  FileValidationStatus,
  DuplicateBezeichnungStatus,
  TimeInfoValidationStatus
} from '../../hooks/useDatasetValidation';

interface ValidationChecklistProps {
  zeitschritteStatus: ZeitschritteStatus;
  inputFilesStatus: FileValidationStatus;
  outputFilesStatus: FileValidationStatus;
  duplicateBezeichnungStatus?: DuplicateBezeichnungStatus;
  timeInfoStatus?: TimeInfoValidationStatus;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
}

interface ChecklistItemProps {
  label: string;
  isComplete: boolean;
  detail?: string;
  subItems?: Array<{
    label: string;
    isComplete: boolean;
  }>;
  invalidFiles?: Array<{
    name: string;
    missingFields: string[];
  }>;
}

const ChecklistItem: React.FC<ChecklistItemProps> = ({
  label,
  isComplete,
  detail,
  subItems,
  invalidFiles
}) => {
  const { t } = useTranslation('training');
  const [isOpen, setIsOpen] = React.useState(false);

  const hasDetails = (subItems && subItems.length > 0) || (invalidFiles && invalidFiles.length > 0);

  return (
    <div className="border-b border-border last:border-b-0">
      <div
        className={`flex items-center gap-2 py-2 px-3 transition-colors duration-150 ${hasDetails ? 'cursor-pointer hover:bg-muted/50' : ''}`}
        onClick={() => hasDetails && setIsOpen(!isOpen)}
      >
        {isComplete ? (
          <FaCircleCheck className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
        ) : (
          <FaCircleXmark className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
        )}
        <span className={`text-xs flex-1 ${isComplete ? 'text-muted-foreground' : 'text-foreground font-medium'}`}>
          {label}
        </span>
        {detail && (
          <span className={`text-[10px] font-medium tabular-nums ${isComplete ? 'text-emerald-600' : 'text-amber-600'}`}>
            {detail}
          </span>
        )}
        {hasDetails && (
          <FaChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </div>

      {/* Expanded details */}
      {isOpen && hasDetails && (
        <div className="bg-muted/40 px-3 py-2 border-t border-border">
          {/* Sub-items (for Zeitschritte fields) */}
          {subItems && subItems.map((item, idx) => (
            <div key={idx} className="flex items-center gap-2 py-0.5">
              {item.isComplete ? (
                <FaCircleCheck className="w-2.5 h-2.5 text-emerald-500" />
              ) : (
                <FaCircleXmark className="w-2.5 h-2.5 text-red-400" />
              )}
              <span className={`text-[10px] ${item.isComplete ? 'text-muted-foreground' : 'text-red-600'}`}>
                {item.label}
              </span>
            </div>
          ))}

          {/* Invalid files list */}
          {invalidFiles && invalidFiles.map((file, idx) => (
            <div key={idx} className="py-1">
              <div className="flex items-center gap-1.5">
                <FaGear className="w-2.5 h-2.5 text-amber-500" />
                <span className="text-[10px] font-medium text-foreground">{file.name}</span>
              </div>
              <div className="ml-4 mt-0.5">
                <span className="text-[10px] text-red-500">
                  {t('training.checklist.missing')}: {file.missingFields.map(f =>
                    t(`training.checklist.fields.${f}`, f)
                  ).join(', ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const ValidationChecklist: React.FC<ValidationChecklistProps> = ({
  zeitschritteStatus,
  inputFilesStatus,
  outputFilesStatus,
  duplicateBezeichnungStatus,
  timeInfoStatus,
  isExpanded = true,
  onToggleExpand
}) => {
  const { t } = useTranslation('training');

  const noDuplicates = !duplicateBezeichnungStatus?.hasDuplicates;
  const timeInfoComplete = timeInfoStatus?.isComplete ?? true;

  const allComplete =
    zeitschritteStatus.isComplete &&
    inputFilesStatus.totalFiles > 0 &&
    inputFilesStatus.invalidFiles.length === 0 &&
    outputFilesStatus.totalFiles > 0 &&
    outputFilesStatus.invalidFiles.length === 0 &&
    noDuplicates &&
    timeInfoComplete;

  // Zeitschritte sub-items
  const zeitschritteSubItems = [
    { label: t('training.checklist.fields.eingabe', 'Eingabe'), isComplete: zeitschritteStatus.hasEingabe },
    { label: t('training.checklist.fields.ausgabe', 'Ausgabe'), isComplete: zeitschritteStatus.hasAusgabe },
    { label: t('training.checklist.fields.zeitschrittweite', 'Zeitschrittweite'), isComplete: zeitschritteStatus.hasZeitschrittweite },
    { label: t('training.checklist.fields.offset', 'Offset'), isComplete: zeitschritteStatus.hasOffset },
    { label: t('training.checklist.fields.saved', 'Saved to database'), isComplete: zeitschritteStatus.isSaved },
  ];

  // Input files status
  const inputFilesComplete = inputFilesStatus.totalFiles > 0 && inputFilesStatus.invalidFiles.length === 0;
  const inputFilesDetail = inputFilesStatus.totalFiles === 0
    ? t('training.checklist.noFiles', 'No files')
    : `${inputFilesStatus.configuredFiles}/${inputFilesStatus.totalFiles}`;

  // Output files status
  const outputFilesComplete = outputFilesStatus.totalFiles > 0 && outputFilesStatus.invalidFiles.length === 0;
  const outputFilesDetail = outputFilesStatus.totalFiles === 0
    ? t('training.checklist.noFiles', 'No files')
    : `${outputFilesStatus.configuredFiles}/${outputFilesStatus.totalFiles}`;

  return (
    <div className={`rounded-lg border max-w-2xl mx-auto overflow-hidden transition-colors duration-200 ${
      allComplete ? 'border-emerald-200/60' : 'border-amber-200/60'
    }`}>
      {/* Header */}
      <div
        className={`flex items-center justify-between px-3 py-2 transition-colors duration-150 ${
          onToggleExpand ? 'cursor-pointer' : ''
        } ${
          allComplete ? 'bg-emerald-50/80' : 'bg-amber-50/80'
        }`}
        onClick={onToggleExpand}
      >
        <div className="flex items-center gap-2">
          {allComplete ? (
            <FaCircleCheck className="w-3.5 h-3.5 text-emerald-600" />
          ) : (
            <FaCircleXmark className="w-3.5 h-3.5 text-amber-600" />
          )}
          <span className={`text-xs font-medium ${allComplete ? 'text-emerald-800' : 'text-amber-800'}`}>
            {allComplete
              ? t('training.checklist.ready', 'Ready to generate')
              : t('training.checklist.incomplete', 'Requirements incomplete')
            }
          </span>
        </div>
        {onToggleExpand && (
          <FaChevronDown className={`w-2.5 h-2.5 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        )}
      </div>

      {/* Checklist items */}
      {isExpanded && (
        <div className="bg-card">
          {/* 1. General Data (Zeitschritte) */}
          <ChecklistItem
            label={t('training.checklist.zeitschritte', 'General Data (Zeitschritte)')}
            isComplete={zeitschritteStatus.isComplete}
            subItems={!zeitschritteStatus.isComplete ? zeitschritteSubItems : undefined}
          />

          {/* 2. Input Files */}
          <ChecklistItem
            label={t('training.checklist.inputFiles', 'Input Files')}
            isComplete={inputFilesComplete}
            detail={inputFilesDetail}
            invalidFiles={inputFilesStatus.invalidFiles.length > 0 ? inputFilesStatus.invalidFiles : undefined}
          />

          {/* 3. Time Information - always show */}
          {timeInfoStatus && (
            <ChecklistItem
              label={t('training.checklist.timeInformation', 'Time information features')}
              isComplete={timeInfoStatus.isComplete}
              invalidFiles={!timeInfoStatus.isComplete ? [
                ...timeInfoStatus.invalidFields.map(field => ({
                  id: field.fieldName,
                  name: field.fieldName,
                  missingFields: field.missingFields
                })),
                ...(!timeInfoStatus.isSaved ? [{
                  id: 'unsaved',
                  name: t('training.checklist.fields.unsaved', 'Unsaved changes'),
                  missingFields: ['saved']
                }] : [])
              ] : undefined}
            />
          )}

          {/* 4. Output Files */}
          <ChecklistItem
            label={t('training.checklist.outputFiles', 'Output Files')}
            isComplete={outputFilesComplete}
            detail={outputFilesDetail}
            invalidFiles={outputFilesStatus.invalidFiles.length > 0 ? outputFilesStatus.invalidFiles : undefined}
          />

          {duplicateBezeichnungStatus?.hasDuplicates && (
            <ChecklistItem
              label={t('training.checklist.uniqueBezeichnung', 'Unique Designations')}
              isComplete={false}
              detail={duplicateBezeichnungStatus.duplicateNames.join(', ')}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ValidationChecklist;
